import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Box, 
  Stack,
  IconButton,
  Typography,
  CssBaseline,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Tooltip
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import { createTheme, ThemeProvider, StyledEngineProvider } from '@mui/material/styles';

// Custom dark theme matching Pickem
const customTheme = createTheme({
  palette: {
    primary: { main: '#ff1744', contrastText: '#ffffff' },
    background: { default: '#121212', paper: '#1e1e1e' },
  },
  typography: { fontFamily: 'Roboto, Arial, sans-serif', h5: { fontWeight: 600 } },
  components: {
    MuiInputLabel: { styleOverrides: { root: { color: '#ffffff' } } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          backgroundColor: '#333333',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
        },
      },
    },
    MuiMenu: { styleOverrides: { paper: { backgroundColor: '#333333', color: '#ffffff' } } },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          '&.Mui-selected': { backgroundColor: '#444444' },
          '&.Mui-selected:hover': { backgroundColor: '#555555' },
          '&:hover': { backgroundColor: '#444444' },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: { root: { color: '#ffffff', '&.Mui-checked': { color: '#ff1744' } } },
    },
  },
});

const Projections = ({ projections, fetchProjections, userName, isAuthenticated }) => {
  // State
  const [selectedLeague, setSelectedLeague] = useState(1); // Default to NFL (1)
  const [selectedPosition, setSelectedPosition] = useState('All');
  const [pageSize, setPageSize] = useState(50);
  const [currentPage, setCurrentPage] = useState(0);
  const [sortModel, setSortModel] = useState([{ field: 'PassingYards', sort: 'desc' }]);
  const [searchQuery, setSearchQuery] = useState('');

  // Position tabs
  const positionTabs = ['All', 'QB', 'RB', 'WR', 'TE'];

  // Fetch projections on mount
  useEffect(() => {
    if (fetchProjections) {
      fetchProjections();
    }
  }, [fetchProjections]);

  // Process and filter projections
  const processedProjections = useMemo(() => {
    if (!Array.isArray(projections)) return [];
    
    // Filter by league first - ensure we're comparing numbers
    let filtered = projections.filter(proj => {
      const projLeagueId = Number(proj?.LeagueId || proj?.leagueId);
      return projLeagueId === Number(selectedLeague);
    });
    
    // Then filter by position
    if (selectedPosition !== 'All') {
      filtered = filtered.filter(proj => 
        proj?.Position === selectedPosition
      );
    }
    
    // Apply search filter
    if (searchQuery.trim()) {
      const searchTerms = searchQuery.toLowerCase().split(/\s+/).filter(term => term);
      filtered = filtered.filter(proj => {
        // Create searchable text from player name and event details
        const playerName = (proj.PlayerName || '').toLowerCase();
        const awayTeam = (proj.AwayShort || '').toLowerCase();
        const homeTeam = (proj.HomeShort || '').toLowerCase();
        const searchableText = `${playerName} ${awayTeam} ${homeTeam}`;
        
        // Check if all search terms are present in the searchable text
        return searchTerms.every(term => searchableText.includes(term));
      });
    }
    
    // Add unique IDs and calculate PPR values
    return filtered.map((proj, idx) => {
      const processedProj = {
        ...proj,
        id: `proj-${idx}-${proj?.PersonId || 'na'}`, // Always unique combining index and PersonId
      };

      // Calculate halfPPR value if not already present
      if (processedProj['1/2PPR'] == null) {
        if (processedProj.Position === 'QB') {
          const passYds = processedProj.PassingYards;
          const passTd = processedProj.PassingTD;
          const int = processedProj.Interceptions;
          const rushYds = processedProj.RushingYards;
          const totalTd = processedProj.Touchdowns;
          
          if (passYds != null && passTd != null && int != null && rushYds != null && totalTd != null) {
            processedProj.halfPPR = (passYds / 25) + (4 * passTd) - int + (0.1 * rushYds) + (6 * totalTd);
          } else {
            processedProj.halfPPR = null;
          }
        } else if (processedProj.Position === 'RB') {
          const rushYds = processedProj.RushingYards;
          const recYds = processedProj.ReceivingYards;
          const rec = processedProj.Receptions;
          const totalTd = processedProj.Touchdowns;
          
          if (rushYds != null &&recYds != null &&rec != null && totalTd != null) {
            processedProj.halfPPR = (0.1 * (rushYds + recYds)) + (6 * totalTd) + (0.5 * rec);
          } else {
            processedProj.halfPPR = null;
          }
        } 
        else if (processedProj.Position === 'WR' || processedProj.Position === 'TE') {
          const rushYds = processedProj.RushingYards || 0;
          const recYds = processedProj.ReceivingYards;
          const rec = processedProj.Receptions;
          const totalTd = processedProj.Touchdowns;
          
          if (recYds != null && rec != null && totalTd != null) {
            processedProj.halfPPR = (0.1 * (rushYds + recYds)) + (6 * totalTd) + (0.5 * rec);
          } else {
            processedProj.halfPPR = null;
          }
        }
        else {
          processedProj.halfPPR = null;
        }
      } else {
        // Use existing value from data
        processedProj.halfPPR = processedProj['1/2PPR'];
      }

      // Calculate PPR value if not already present
      if (processedProj.PPR == null) {
        if (processedProj.Position === 'QB') {
          const passYds = processedProj.PassingYards;
          const passTd = processedProj.PassingTD;
          const int = processedProj.Interceptions;
          const rushYds = processedProj.RushingYards;
          const totalTd = processedProj.Touchdowns;
          
          if (passYds != null && passTd != null && int != null && rushYds != null && totalTd != null) {
            processedProj.fullPPR = (passYds / 25) + (4 * passTd) - int + (0.1 * rushYds) + (6 * totalTd);
          } else {
            processedProj.fullPPR = null;
          }
        } else if (processedProj.Position === 'RB') {
          const rushYds = processedProj.RushingYards;
          const recYds = processedProj.ReceivingYards;
          const rec = processedProj.Receptions;
          const totalTd = processedProj.Touchdowns;
          
          if (rushYds != null &&recYds != null &&rec != null && totalTd != null) {
            processedProj.fullPPR = (0.1 * (rushYds + recYds)) + (6 * totalTd) +rec;
          } else {
            processedProj.fullPPR = null;
          }
        } 
        else if (processedProj.Position === 'WR' || processedProj.Position === 'TE') {
          const rushYds = processedProj.RushingYards || 0;
          const recYds = processedProj.ReceivingYards;
          const rec = processedProj.Receptions;
          const totalTd = processedProj.Touchdowns;
          
          if (recYds != null && rec != null && totalTd != null) {
            processedProj.fullPPR = (0.1 * (rushYds + recYds)) + (6 * totalTd) + rec;
          } else {
            processedProj.fullPPR = null;
          }
        }else {
          processedProj.fullPPR = null;
        }
      } else {
        // Use existing value from data
        processedProj.fullPPR = processedProj.PPR;
      }

      return processedProj;
    });
  }, [projections, selectedPosition, selectedLeague, searchQuery]);

  // Column definitions - matching exact database fields
  const columns = [
    {
      field: 'Position',
      headerName: 'Pos',
      flex: 0.5,
      minWidth: 60,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          Pos
        </Typography>
      ),
      valueFormatter: (value) => value || '-',
    },
    {
      field: 'PlayerName',
      headerName: 'Player',
      flex: 2,
      minWidth: 180,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          Player
        </Typography>
      ),
      renderCell: (params) => {
        if (!params?.row) return '-';
        return (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1, 
            height: '100%',
            width: '100%'
          }}>
            {params.row.teamLogo && (
              <img 
                src={params.row.teamLogo} 
                alt="" 
                style={{ width: 20, height: 20, objectFit: 'contain' }}
              />
            )}
            <Typography sx={{ fontSize: '0.875rem' }}>{params.value || '-'}</Typography>
          </Box>
        );
      },
    },
    {
      field: 'Event',
      headerName: 'Event',
      flex: 1.5,
      minWidth: 180,
      sortComparator: (v1, v2, param1, param2) => {
        // Sort by EventStart date
        const date1 = param1.api.getCellValue(param1.id, 'EventStart');
        const date2 = param2.api.getCellValue(param2.id, 'EventStart');
        
        if (!date1 && !date2) return 0;
        if (!date1) return 1;
        if (!date2) return -1;
        
        return new Date(date1).getTime() - new Date(date2).getTime();
      },
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          Event
        </Typography>
      ),
      renderCell: (params) => {
        if (!params?.row) return '-';
        
        const away = params.row.AwayShort || '';
        const home = params.row.HomeShort || '';
        const eventStart = params.row.EventStart
          ? new Date(params.row.EventStart).toLocaleString('en-US', {
              year: '2-digit',
              month: 'numeric',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              hour12: true,
            }).replace(',', '')
          : '';
        
        return (
          <Box sx={{ 
            whiteSpace: 'pre-line', 
            textAlign: 'left', 
            display: 'flex', 
            flexDirection: 'column', 
            justifyContent: 'center', 
            height: '100%' 
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', paddingLeft: '8px' }}>
              {params.row.AwayLogo && (
                <img
                  src={params.row.AwayLogo}
                  alt={`${away} Logo`}
                  style={{ width: 'auto', height: '14px', paddingRight: '2px', objectFit: 'contain' }}
                  loading="lazy"
                />
              )}
              <Typography sx={{ fontSize: '0.875rem' }}>{away}</Typography>
              <Typography sx={{ fontSize: '0.75rem', mx: '2px' }}>@</Typography>
              {params.row.HomeLogo && (
                <img
                  src={params.row.HomeLogo}
                  alt={`${home} Logo`}
                  style={{ width: 'auto', height: '14px', paddingRight: '2px', objectFit: 'contain' }}
                  loading="lazy"
                />
              )}
              <Typography sx={{ fontSize: '0.875rem' }}>{home}</Typography>
            </Box>
            <Typography sx={{ fontSize: '0.875rem', paddingLeft: '8px', color: '#BBBBBB' }}>
              {eventStart}
            </Typography>
          </Box>
        );
      },
    },
        {
      field: 'PassingYards',
      headerName: 'Pass Yds',
      type: 'number',
      flex: 1,
      minWidth: 80,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          Pass Yds
        </Typography>
      ),
      valueFormatter: (value) => {
        return value != null ? value.toFixed(1) : '-';
      },
    },
        {
      field: 'PassAttempts',
      headerName: 'Pass Attempts',
      type: 'number',
      flex: 1,
      minWidth: 80,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          Pass Att
        </Typography>
      ),
      valueFormatter: (value) => {
        return value != null ? value.toFixed(2) : '-';
      },
    },
    {
      field: 'Completions',
      headerName: 'Completions',
      type: 'number',
      flex: 1,
      minWidth: 80,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          Pass Comp
        </Typography>
      ),
      valueFormatter: (value) => {
        return value != null ? value.toFixed(2) : '-';
      },
    },
    {
      field: 'PassingTD',
      headerName: 'Pass TD',
      type: 'number',
      flex: 1,
      minWidth: 80,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          Pass TD
        </Typography>
      ),
      valueFormatter: (value) => {
        return value != null ? value.toFixed(2) : '-';
      },
    },
    {
      field: 'Interceptions',
      headerName: 'INT',
      type: 'number',
      flex: 1,
      minWidth: 60,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          INT
        </Typography>
      ),
      valueFormatter: (value) => {
        return value != null ? value.toFixed(2) : '-';
      },
    },
        {
      field: 'RushAttempts',
      headerName: 'Rush Attempts',
      type: 'number',
      flex: 1,
      minWidth: 80,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          Rush Att
        </Typography>
      ),
      valueFormatter: (value) => {
        return value != null ? value.toFixed(2) : '-';
      },
    },
    {
      field: 'RushingYards',
      headerName: 'Rush Yds',
      type: 'number',
      flex: 1,
      minWidth: 80,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          Rush Yds
        </Typography>
      ),
      valueFormatter: (value) => {
        return value != null ? value.toFixed(1) : '-';
      },
    },
    {
      field: 'Receptions',
      headerName: 'Rec',
      type: 'number',
      flex: 1,
      minWidth: 60,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          Rec
        </Typography>
      ),
      valueFormatter: (value) => {
        return value != null ? value.toFixed(2) : '-';
      },
    },
    {
      field: 'ReceivingYards',
      headerName: 'Rec Yds',
      type: 'number',
      flex: 1,
      minWidth: 80,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          Rec Yds
        </Typography>
      ),
      valueFormatter: (value) => {
        return value != null ? value.toFixed(1) : '-';
      },
    },
    {
      field: 'Touchdowns',
      headerName: 'Total TD',
      type: 'number',
      flex: 1,
      minWidth: 80,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          Total TD
        </Typography>
      ),
      valueFormatter: (value) => {
        return value != null ? value.toFixed(2) : '-';
      },
    },
    {
      field: 'halfPPR',
      headerName: '0.5 PPR',
      type: 'number',
      flex: 1,
      minWidth: 80,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          0.5 PPR
        </Typography>
      ),
      valueFormatter: (value) => {
        return value != null ? value.toFixed(2) : '-';
      },
    },
    {
      field: 'fullPPR',
      headerName: 'PPR',
      type: 'number',
      flex: 1,
      minWidth: 80,
      renderHeader: () => (
        <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>
          PPR
        </Typography>
      ),
      valueFormatter: (value) => {
        return value != null ? value.toFixed(2) : '-';
      },
    },
  ];

  // Export to CSV
  const exportToCSV = useCallback(() => {
    if (!processedProjections.length || !isAuthenticated) return;

    const headers = columns.map(col => col.headerName);
    const rows = processedProjections.map(proj => {
      return columns.map(col => {
        const value = proj[col.field];
        
        if (value == null) return '';
        
        // Apply the column's valueFormatter if it exists
        if (col.valueFormatter) {
          return col.valueFormatter(value);
        }
        
        // Default formatting for values without a formatter
        if (typeof value === 'number') return value.toFixed(1);
        return String(value);
      }).map(value => `"${String(value).replace(/"/g, '""')}"`).join(',');
    });

    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const leagueName = selectedLeague === 1 ? 'nfl' : 'cfb';
    link.download = `${leagueName}_projections_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [processedProjections, columns, selectedLeague, isAuthenticated]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    const newPosition = positionTabs[newValue];
    setSelectedPosition(newPosition);
    setCurrentPage(0); // Reset to first page when changing position
    
    // Update sort based on position
    let newSortField = 'PassingYards'; // Default for All and QB
    if (newPosition === 'RB') {
      newSortField = 'RushingYards';
    } else if (newPosition === 'WR' || newPosition === 'TE') {
      newSortField = 'ReceivingYards';
    }
    
    setSortModel([{ field: newSortField, sort: 'desc' }]);
  };

  return (
    <ThemeProvider theme={customTheme}>
      <StyledEngineProvider injectFirst>
        <CssBaseline />
        <Box sx={{ 
          width: '100%', 
          height: '100vh',
          backgroundColor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          {/* Filter Section */}
          <Box className="filter-section" sx={{ 
            padding: '16px',
            backgroundColor: '#121212',
            borderBottom: '1px solid #333'
          }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={2}>
                {/* League Dropdown */}
                <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
                  <InputLabel id="league-filter-label" shrink sx={{ color: '#ffffff' }}>
                    League
                  </InputLabel>
                  <Select
                    labelId="league-filter-label"
                    label="League"
                    notched
                    value={selectedLeague}
                    onChange={(e) => {
                      setSelectedLeague(e.target.value);
                      setCurrentPage(0); // Reset pagination when changing league
                    }}
                  >
                    <MenuItem value={1}>NFL</MenuItem>
                    <MenuItem value={2}>CFB</MenuItem>
                  </Select>
                </FormControl>
                
                {/* Position Tabs */}
                <Tabs 
                  value={positionTabs.indexOf(selectedPosition)}
                  onChange={handleTabChange}
                  sx={{
                    minHeight: 38,
                    '& .MuiTab-root': { 
                      color: '#ffffff',
                      minWidth: 60,
                      minHeight: 32,
                      padding: '4px 12px',
                      '&.Mui-selected': { color: '#ff1744' }
                    },
                    '& .MuiTabs-indicator': { backgroundColor: '#ff1744' }
                  }}
                >
                  {positionTabs.map((position) => (
                    <Tab key={position} label={position} />
                  ))}
                </Tabs>
                
                {/* Search Bar */}
                <TextField
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search player or matchup"
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#BBBBBB' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    width: 225,
                    '& .MuiInputBase-input': { 
                      color: '#ffffff' 
                    },
                    '& .MuiInputBase-input::placeholder': { 
                      color: '#BBBBBB', 
                      opacity: 1 
                    },
                    '& .MuiInputLabel-root': { 
                      fontSize: '0.875rem' 
                    },
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#ffffff',
                      },
                      '&:hover fieldset': {
                        borderColor: '#ff1744',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#ff1744',
                      },
                    },
                  }}
                />
              </Stack>
              
              <Tooltip 
                title={
                  !isAuthenticated 
                    ? "Please log in to enable exports" 
                    : processedProjections.length === 0 
                    ? "No data to export" 
                    : ""
                }
                arrow
              >
                <span>
                  <IconButton
                    onClick={exportToCSV}
                    disabled={processedProjections.length === 0 || !isAuthenticated}
                    sx={{
                      color: '#bbbbbb',
                      padding: '4px',
                      '&:hover': { backgroundColor: 'rgba(187, 187, 187, 0.1)' },
                      '&.Mui-disabled': { color: '#666666' },
                    }}
                  >
                    <DownloadIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Box>

          {/* Data Grid */}
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            <Box className="table-section" sx={{ height: '100%' }}>
              <Box className="data-grid-container" sx={{ height: '100%', boxSizing: 'border-box' }}>
                <DataGridPro
                  rows={processedProjections}
                  columns={columns}
                  pageSize={pageSize}
                  onPageSizeChange={setPageSize}
                  rowsPerPageOptions={[50, 100, 250]}
                  pagination
                  paginationMode="client"
                  page={currentPage}
                  onPageChange={setCurrentPage}
                  sortingMode="client"
                  sortModel={sortModel}
                  onSortModelChange={setSortModel}
                  disableSelectionOnClick
                  disableColumnFilter
                  disableColumnMenu
                  loading={!projections || projections.length === 0}
                  getRowId={(row) => row.id}
                  hideFooterSelectedRowCount
                  sx={{
                    '& .MuiDataGrid-columnHeaders': { 
                      backgroundColor: '#1e1e1e', 
                      borderBottom: '2px solid #444',
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                    },
                    '& .MuiDataGrid-columnHeader:focus': { outline: 'none' },
                    '& .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
                    '& .MuiDataGrid-columnHeaderTitle': {
                      color: '#ffffff !important',
                      userSelect: 'none',
                    },
                    '& .MuiDataGrid-cell': {
                      color: '#ffffff !important',
                      fontSize: '0.875rem',
                      userSelect: 'none',
                      borderRight: '1px solid rgba(255, 255, 255, 0.3)',
                    },
                    '& .MuiDataGrid-cell:last-child': {
                      borderRight: 'none',
                    },
                    '& .MuiDataGrid-cell:focus': { outline: 'none' },
                    '& .MuiDataGrid-cell:focus-within': { outline: 'none' },
                    '& .MuiDataGrid-row:hover': { backgroundColor: '#1a1a1a' },
                    '& .MuiDataGrid-row.Mui-selected': {
                      backgroundColor: '#1a1a1a',
                      '&:hover': { backgroundColor: '#1a1a1a' },
                    },
                    '& .MuiDataGrid-columnSeparator': { 
                      visibility: 'visible',
                      color: 'rgba(255, 255, 255, 0.3)',
                    },
                    '& .MuiDataGrid-sortIcon': { color: '#ffffff !important' },
                    '& .MuiTablePagination-root, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                      color: '#ffffff !important',
                    },
                    '& .MuiSelect-icon': { color: '#ffffff !important' },
                    '& .MuiDataGrid-menuIconButton': { color: '#ffffff !important' },
                    '& .MuiDataGrid-root': {
                      overflowY: 'auto !important',
                      margin: 0,
                      boxSizing: 'border-box',
                    },
                    '& .MuiDataGrid-virtualScroller': {
                      overflowY: 'auto !important',
                    },
                    '& .MuiDataGrid-main': {
                      padding: 0,
                      boxSizing: 'border-box',
                    },
                    '& .MuiDataGrid-footerContainer': {
                      backgroundColor: '#1e1e1e',
                      color: '#ffffff !important',
                      position: 'sticky',
                      bottom: 0,
                      zIndex: 1,
                      boxSizing: 'border-box',
                    },
                  }}
                  rowBuffer={10}
                  rowThreshold={5}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </StyledEngineProvider>
    </ThemeProvider>
  );
};

export default Projections;